<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" sm="10" md="8" lg="6">
        <h3 class="report-title">{{ reportTitle }}</h3>

        <!-- Grouping toggle -->
        <div class="d-flex justify-center flex-wrap mb-2" style="gap: 6px;">
          <v-btn size="small" color="secondary" variant="outlined" :active="groupBy === 'day'"   @click="groupBy = 'day'">Days</v-btn>
          <v-btn size="small" color="secondary" variant="outlined" :active="groupBy === 'week'"  @click="groupBy = 'week'">Weeks</v-btn>
          <v-btn size="small" color="secondary" variant="outlined" :active="groupBy === 'month'" @click="groupBy = 'month'">Months</v-btn>
        </div>

        <!-- Date range buttons -->
        <div class="d-flex justify-center flex-wrap mb-2" style="gap: 6px;">
          <v-btn size="small" color="primary" variant="outlined" :active="dateMode === 'all'"    @click="setAllTime">All Time</v-btn>
          <v-btn size="small" color="primary" variant="outlined" :active="dateMode === 'ytd'"    @click="setYearToDate">YTD</v-btn>
          <v-btn size="small" color="primary" variant="outlined" :active="dateMode === 'last12'" @click="setLast12Months">Last 12</v-btn>
          <v-btn size="small" color="primary" variant="outlined" :active="dateMode === 'custom'" @click="setCustom">Custom</v-btn>
        </div>

        <!-- Custom date pickers -->
        <div v-if="dateMode === 'custom'" class="d-flex justify-center flex-wrap mb-3" style="gap: 8px;">
          <v-menu v-model="fromDateMenu" :close-on-content-click="false" min-width="auto">
            <template #activator="{ props }">
              <v-text-field
                :model-value="fromDate ? formatDate(fromDate) : ''"
                @update:model-value="val => fromDate = parseDate(val)"
                label="From" prepend-icon="mdi-calendar" readonly v-bind="props"
                style="max-width: 180px;" density="compact" hide-details
              />
            </template>
            <v-date-picker v-model="fromDate" @update:model-value="fromDateMenu = false" show-adjacent-months />
          </v-menu>
          <v-menu v-model="toDateMenu" :close-on-content-click="false" min-width="auto">
            <template #activator="{ props }">
              <v-text-field
                :model-value="toDate ? formatDate(toDate) : ''"
                @update:model-value="val => toDate = parseDate(val)"
                label="To" prepend-icon="mdi-calendar" readonly v-bind="props"
                style="max-width: 180px;" density="compact" hide-details
              />
            </template>
            <v-date-picker v-model="toDate" @update:model-value="toDateMenu = false" show-adjacent-months />
          </v-menu>
        </div>

        <!-- Session type filter -->
        <div class="d-flex justify-center flex-wrap mb-3" style="gap: 6px;">
          <v-btn size="small" color="primary" variant="outlined" :active="typefilter === null"       @click="typefilter = null">All</v-btn>
          <v-btn size="small" color="primary" variant="outlined" :active="typefilter === CASH"       @click="typefilter = CASH">Cash</v-btn>
          <v-btn size="small" color="primary" variant="outlined" :active="typefilter === TOURNAMENT" @click="typefilter = TOURNAMENT">Tournament</v-btn>
        </div>

        <div v-if="rows.length > 0">
          <v-table density="compact">
            <colgroup>
              <col style="width: 40%">
              <col style="width: 20%">
              <col style="width: 40%">
            </colgroup>
            <thead>
              <tr>
                <th class="text-left">{{ periodLabel }}</th>
                <th class="text-center">Sessions</th>
                <th class="text-right">Total Net</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in rows" :key="i" class="clickable-row" @click="handleRowClick(row)">
                <td class="text-left">{{ row.dateLabel }}</td>
                <td class="text-center">{{ row.sessions }}</td>
                <td class="text-right" :class="row.net >= 0 ? 'green-text' : 'red-text'">
                  {{ formatNet(row.net) }}
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>
        <div v-else class="text-center text-medium-emphasis py-4">
          No sessions in this date range.
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useSessionStore } from '@/stores/sessions';
import { useDateFilter } from '@/compostables/useDateFilter';
import { CASH, TOURNAMENT } from '@/constants/constants';

const emit = defineEmits(['drilldown']);

const sessionStore = useSessionStore();
const sessionlist = computed(() => sessionStore.sessionlist);

const {
  dateMode, fromDate, toDate, fromDateMenu, toDateMenu,
  filteredSessions, setAllTime, setYearToDate, setLast12Months, setCustom,
  formatDate, parseDate,
} = useDateFilter(sessionlist);

const typefilter = ref(null);
const groupBy = ref('day');

const reportTitle = computed(() => {
  if (groupBy.value === 'week') return 'Top Weeks';
  if (groupBy.value === 'month') return 'Top Months';
  return 'Top Days';
});

const periodLabel = computed(() => {
  if (groupBy.value === 'week') return 'Week';
  if (groupBy.value === 'month') return 'Month';
  return 'Date';
});

const typefilteredSessions = computed(() =>
  typefilter.value ? filteredSessions.value.filter(s => s.type === typefilter.value) : filteredSessions.value
);

function getWeekStart(d) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day; // back to Monday
  date.setDate(date.getDate() + diff);
  return date;
}

function toDateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const rows = computed(() => {
  const map = {};
  for (const s of typefilteredSessions.value) {
    if (s.net == null || !s.sessiondate) continue;
    const d = s.sessiondate instanceof Date ? s.sessiondate : new Date(s.sessiondate);

    let key, periodStart, periodEnd, dateLabel;

    if (groupBy.value === 'month') {
      key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!map[key]) {
        periodStart = new Date(d.getFullYear(), d.getMonth(), 1);
        periodEnd   = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        dateLabel   = periodStart.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
        map[key] = { key, net: 0, sessions: 0, periodStart, periodEnd, dateLabel };
      }
    } else if (groupBy.value === 'week') {
      const weekStart = getWeekStart(d);
      key = toDateKey(weekStart);
      if (!map[key]) {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        dateLabel = 'Week of ' + weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        map[key] = { key, net: 0, sessions: 0, periodStart: weekStart, periodEnd: weekEnd, dateLabel };
      }
    } else {
      key = toDateKey(d);
      if (!map[key]) {
        const day = new Date(key + 'T00:00:00');
        dateLabel = day.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        map[key] = { key, net: 0, sessions: 0, periodStart: day, periodEnd: day, dateLabel };
      }
    }

    map[key].net += s.net;
    map[key].sessions += 1;
  }
  return Object.values(map).sort((a, b) => b.net - a.net);
});

function handleRowClick(row) {
  const from = new Date(row.periodStart);
  from.setHours(0, 0, 0, 0);
  const to = new Date(row.periodEnd);
  to.setHours(23, 59, 59, 999);
  emit('drilldown', {
    fromDate:      { date: from },
    toDate:        { date: to },
    typefilter:    typefilter.value,
    gamefilter:    [],
    placefilter:   [],
    bettypefilter: [],
  });
}

function formatNet(val) {
  const n = Number(val).toFixed(2);
  return val >= 0 ? `$${n}` : `-$${Math.abs(Number(n)).toFixed(2)}`;
}
</script>

<style scoped>
.report-title {
  margin-bottom: 12px;
  text-align: center;
}
.clickable-row {
  cursor: pointer;
}
.clickable-row:hover td {
  background-color: rgba(0, 0, 0, 0.04);
}
.green-text { color: #2e7d32; }
.red-text   { color: #c62828; }
</style>
